import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Mail from '@mui/icons-material/Mail';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Sms from '@mui/icons-material/Sms';
import ToggleOffOutlined from '@mui/icons-material/ToggleOffOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  AuthMethod,
  CompanyShareableType,
  deleteV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersId,
  ModelError_Item,
  ModelError_Response,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { cloneDeep } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import DeactivateConfirmationDialog from '~components/DeactivateConfirmationDialog/DeactivateConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import { InvitesToSendToggleGroup } from '~components/FormFields/v2/ToggleButtonInput';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import UserNotificationsPreferenceForm, {
  UserNotificationsPreferenceDTO,
  UserNotificationsPreferenceHandler,
} from '~components/UserForm/UserNotificationsPreferenceForm';
import {
  UserForm,
  UserFormHandler,
  UserFormStateChangeProps,
  UserValidationDTOWithSitesDiff,
} from '~components/UserForm/v2/UserForm';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Company } from '~hooks/useCompany/models';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { useCompanyShares } from '~hooks/useCompanyShares/useCompanyShares';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import useManagedCompanies from '~hooks/useManagedCompanies/useManagedCompanies';
import { useResourceScopes } from '~hooks/useResourceScopes/useResourceScopes';
import { User } from '~hooks/useUsers/models';
import { useUserRoles } from '~hooks/useUsers/useUserRoles';
import useUsers from '~hooks/useUsers/useUsers';
import connection from '~services/connectionModule';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { dateFormat } from '~utils/dateTime';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { formatPhoneForDisplay } from '~utils/utilFunctions';

interface ManagedVendorUsersTabProps {
  managedCompanyId: string;
  managedCompany: Company;
}

interface DialogState {
  isOpen: boolean;
  availableAuthMethods: AuthMethod[];
}

interface InvitesToSendState {
  email: { isDisabled: boolean; shouldSend: boolean };
  sms: { isDisabled: boolean; shouldSend: boolean };
}

export const ManagedVendorUsersTab = observer(
  ({ managedCompanyId, managedCompany }: ManagedVendorUsersTabProps) => {
    const { userStore, toasterStore } = useStores();
    const companyId = getEffectiveUserCompanyId(userStore);

    const userPermissions = useMemo(() => {
      return userStore.getPermissions();
    }, [userStore]);

    // Hooks
    const {
      getCompanyUsers,
      getUserById,
      createUser,
      updateUser,
      sendInvitation,
      isUpdating: isUpdatingUsers,
    } = useUsers();

    const { getManagedCompanyById } = useManagedCompanies();

    const {
      getCompanySharesByCompanyId,
      createCompanyShareByCompanyId,
      deleteCompanyShare,
      updateCompanyShare,
      isUpdating: isUpdatingCompanyShares,
    } = useCompanyShares();

    const { saveUserSitesDiff } = useResourceScopes();
    const { getUserRoles } = useUserRoles();

    // State
    const [searchQuery, setSearchQuery] = useState('');
    const [hasRequestedFirstPage, setHasRequestedFirstPage] = useState(false);

    const {
      goToPage: goToUsersPage,
      isFetching: isFetchingUsers,
      pagination: usersPagination,
      reloadCurrentPage: reloadUsersPage,
      rows: users,
      startOver: startUsersOver,
    } = useCursorPagination<User>({
      fetchPage: (params) =>
        getCompanyUsers({ companyId, managedCompanyId, searchQuery, ...params }),
      limit: MAX_API_PAGE_SIZE,
      onFetchError: (error) =>
        reportRequestError(error, t('error_messages.users.failed_to_fetch')),
    });

    // Dialog state
    const [userFormDialogState, setUserFormDialogState] = useState<DialogState>({
      isOpen: false,
      availableAuthMethods: [],
    });

    // Selected entities
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [selectedUser, setSelectedUser] = useState<User | undefined>();
    const [companyShares, setCompanyShares] = useState<CompanyShare[]>([]);

    // Form tracking
    const [userFormIsDirty, setUserFormIsDirty] = useState(false);
    const [
      userNotificationsPreferenceFormIsDirty,
      setUserNotificationsPreferenceFormIsDirty,
    ] = useState(false);
    const [invitesToSend, setInvitesToSend] = useState<InvitesToSendState>();

    // Error handling
    const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);
    const [userIdToDelete, setUserIdToDelete] = useState<string>();

    // Refs
    const updateUserDialogRef = useRef<UserFormHandler>(null);
    const userNotificationsPreferenceRef =
      useRef<UserNotificationsPreferenceHandler>(null);

    // Computed values
    const isUpdating = isUpdatingUsers || isUpdatingCompanyShares;
    const isDirty = userFormIsDirty || userNotificationsPreferenceFormIsDirty;

    // Load user roles on mount
    useEffect(() => {
      getUserRoles();
    }, []);

    useEffect(() => {
      if (managedCompanyId) {
        setHasRequestedFirstPage(true);
        void startUsersOver();
      }
    }, [companyId, managedCompanyId, searchQuery]);

    // Dialog handlers
    const createNewUser = async () => {
      setSelectedId(null);
      setSelectedUser(undefined);
      setCompanyShares([]);

      const company = await getManagedCompanyById({ companyId, managedCompanyId });
      if (company) {
        setUserFormDialogState({
          isOpen: true,
          availableAuthMethods: company.authMethods,
        });
        setInvitesToSend({
          email: { isDisabled: true, shouldSend: false },
          sms: { isDisabled: true, shouldSend: false },
        });
      }
    };

    const editRow = async (id: string) => {
      setSelectedId(id);
      const fetchedUser = await getUserById(id, companyId, managedCompanyId);
      setSelectedUser(fetchedUser as User);

      if (fetchedUser?.company?.id) {
        const company = await getManagedCompanyById({
          companyId,
          managedCompanyId: String(fetchedUser.company.id),
        });

        if (company) {
          setUserFormDialogState({
            isOpen: true,
            availableAuthMethods: company.authMethods,
          });

          if (fetchedUser.isActiveMember) {
            setInvitesToSend(undefined);
          } else {
            setInvitesToSend({
              email: { isDisabled: true, shouldSend: false },
              sms: { isDisabled: true, shouldSend: false },
            });
          }
        }
      }

      // Fetch company shares
      await getCompanySharesByCompanyId({
        companyId,
        managedCompanyId,
        company_shareable_type: 'User' as CompanyShareableType,
        company_shareable_id: id,
        sender_company_id: managedCompanyId,
        callBack: setCompanyShares,
      });
    };

    const handleClose = () => {
      setUserFormDialogState({
        isOpen: false,
        availableAuthMethods: [],
      });
      setSelectedId(null);
      setSelectedUser(undefined);
      setCompanyShares([]);
      setAsyncErrors([]);
      setUserFormIsDirty(false);
      setUserNotificationsPreferenceFormIsDirty(false);
      setInvitesToSend(undefined);
    };

    const onSuccess = (userData: UserValidationDTOWithSitesDiff) => {
      const name = `${userData.firstName} ${userData.lastName}`;
      toasterStore.push(
        alert(
          selectedId
            ? t('user.user_updated', { name })
            : t('user.user_created', { name }),
          AlertTypes.success,
        ),
      );
      handleClose();
      if (selectedId) {
        void reloadUsersPage();
      } else {
        void goToUsersPage();
      }
    };

    // Company shares CRUD helper
    const handleCompanySharesUpdate = async (data: UserValidationDTOWithSitesDiff) => {
      const companySharesPresentOnModalOpen = companyShares;
      const companySharesPresentOnSubmit = data.companyShares?.filter(
        (submittedCompanyShare) => !!submittedCompanyShare.receiverCompany.id,
      );

      const companySharesToCreate = companySharesPresentOnSubmit?.filter(
        (submitted) =>
          !companySharesPresentOnModalOpen.some(
            (existing) => existing.receiverCompany.id === submitted.receiverCompany.id,
          ),
      );

      const companySharesToUpdate = companySharesPresentOnSubmit?.filter(
        (submitted) => submitted.id,
      );

      const companySharesToDelete = companySharesPresentOnModalOpen.filter(
        (existing) =>
          companySharesPresentOnSubmit &&
          !companySharesPresentOnSubmit.some(
            (submitted) => existing.receiverCompany.id === submitted.receiverCompany.id,
          ),
      );

      // Execute create operations
      if (companySharesToCreate) {
        for (const cs of companySharesToCreate) {
          if (cs.receiverCompany.id) {
            await createCompanyShareByCompanyId({
              companyId,
              managedCompanyId,
              company_shareable_id: selectedId!,
              receiver_company_id: cs.receiverCompany.id,
              company_shareable_type: 'User' as CompanyShareableType,
              rate_override: cs.rateOverride ?? false,
            });
          }
        }
      }

      // Execute update operations
      if (companySharesToUpdate) {
        for (const cs of companySharesToUpdate) {
          if (cs.id) {
            await updateCompanyShare(
              {
                companyShareId: cs.id,
                rate_override: cs.rateOverride ?? false,
              },
              companyId,
              managedCompanyId,
            );
          }
        }
      }

      // Execute delete operations
      if (companySharesToDelete) {
        for (const cs of companySharesToDelete) {
          if (cs.id) {
            await deleteCompanyShare(cs.id, companyId, managedCompanyId);
          }
        }
      }
    };

    // Form submission
    const onSubmitForm = async () => {
      const userData = await new Promise<UserValidationDTOWithSitesDiff | undefined>(
        (resolve) => {
          updateUserDialogRef.current?.submit((data) => resolve(data));
        },
      );

      const userNotificationsPreferenceData = await new Promise<
        UserNotificationsPreferenceDTO | undefined
      >((resolve) => {
        if (selectedUser?.id && userNotificationsPreferenceRef.current) {
          userNotificationsPreferenceRef.current?.onSubmit?.((data) => resolve(data));
        } else {
          resolve(undefined);
        }
      });

      if (userData) {
        await onSubmitCallback(userData, userNotificationsPreferenceData);
      }
    };

    const onSubmitCallback = async (
      data: UserValidationDTOWithSitesDiff,
      userNotificationsPreferenceData?: UserNotificationsPreferenceDTO,
    ) => {
      const shouldSendEmailInvite = Boolean(
        !invitesToSend?.email.isDisabled && invitesToSend?.email.shouldSend,
      );
      const shouldSendSmsInvite = Boolean(
        !invitesToSend?.sms.isDisabled && invitesToSend?.sms.shouldSend,
      );

      setAsyncErrors([]);

      if (selectedId) {
        // UPDATE flow
        try {
          await updateUser(
            { id: selectedId, ...data } as unknown as User,
            userNotificationsPreferenceData,
            companyId,
            managedCompanyId,
          );

          // Handle company shares CRUD
          await handleCompanySharesUpdate(data);

          // Send invitations
          if (shouldSendEmailInvite) {
            await sendInvitation(selectedId, 'email', companyId, managedCompanyId);
          }
          if (shouldSendSmsInvite) {
            await sendInvitation(selectedId, 'sms', companyId, managedCompanyId);
          }

          // Save site permissions
          await saveUserSitesDiff(
            selectedId,
            data.sitesDiff,
            companyId,
            managedCompanyId,
          );

          onSuccess(data);
        } catch (error) {
          if (isAxiosError(error)) {
            const e = error as AxiosError<ModelError_Response>;
            setAsyncErrors(e.response?.data.error.errors || []);
          } else {
            toasterStore.push(
              alert(t('error_messages.users.failed_to_update'), AlertTypes.error),
            );
          }
        }
      } else {
        // CREATE flow
        try {
          const newUser = await createUser(
            data as unknown as User,
            {
              email: shouldSendEmailInvite,
              sms: shouldSendSmsInvite,
            },
            companyId,
            managedCompanyId,
          );

          // Create company shares
          if (newUser && data.companyShares?.length) {
            for (const cs of data.companyShares) {
              if (cs.receiverCompany.id) {
                await createCompanyShareByCompanyId({
                  companyId,
                  managedCompanyId,
                  receiver_company_id: cs.receiverCompany.id,
                  company_shareable_id: newUser.id,
                  company_shareable_type: 'User' as CompanyShareableType,
                  rate_override: cs.rateOverride ?? false,
                });
              }
            }
          }

          // Save site permissions
          if (newUser) {
            await saveUserSitesDiff(
              newUser.id,
              data.sitesDiff,
              companyId,
              managedCompanyId,
            );
          }

          onSuccess(data);
        } catch (error) {
          if (isAxiosError(error)) {
            const e = error as AxiosError<ModelError_Response>;
            setAsyncErrors(e.response?.data.error.errors || []);
          } else {
            toasterStore.push(
              alert(t('error_messages.users.failed_to_create'), AlertTypes.error),
            );
          }
        }
      }
    };

    // Deactivate user
    const handleDeleteUser = useCallback(async () => {
      if (!userIdToDelete) return Promise.resolve();

      try {
        await deleteV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersId({
          path: {
            'company-id': companyId,
            'managed-company-id': managedCompanyId,
            id: userIdToDelete,
          },
        });

        toasterStore.push(
          alert(t('administration.users.user_deactivated'), AlertTypes.success),
        );
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.users.failed_to_deactivate'),
        );

        return;
      }

      setUserIdToDelete(undefined);
      await reloadUsersPage();
    }, [userIdToDelete, toasterStore, reloadUsersPage, companyId, managedCompanyId]);

    // Resend invitation
    const resendInvite = async (rowId: string, method: 'email' | 'sms') => {
      if (rowId) {
        try {
          await sendInvitation(rowId, method, companyId, managedCompanyId);
          toasterStore.push(alert(t('user.invitation_sent'), AlertTypes.success));
        } catch {
          // Error toast is handled in the hook.
        }
      }
    };

    // Form state change handlers
    const onFormStateChange = ({
      isDirty,
      hasValidEmail,
      hasValidPhone,
    }: UserFormStateChangeProps) => {
      setUserFormIsDirty(isDirty);

      if (invitesToSend) {
        setInvitesToSend((state) => {
          const newState = cloneDeep(state);
          if (newState) {
            newState.email = { ...newState.email, isDisabled: !hasValidEmail };
            newState.sms = { ...newState.sms, isDisabled: !hasValidPhone };
          }
          return newState;
        });
      }
    };

    const onUserNotificationsPreferenceFormStateChange = ({
      isDirty,
    }: {
      isDirty: boolean;
    }) => {
      setUserNotificationsPreferenceFormIsDirty(isDirty);
    };

    const onChangeInvite = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
      const name = event.target.name as 'email' | 'sms';
      const checked = event.target.checked;
      setInvitesToSend((state) => {
        const newState = cloneDeep(state);
        if (newState) {
          newState[name] = {
            ...newState[name],
            shouldSend: checked,
          };
        }
        return newState;
      });
    }, []);

    // Column definitions
    const columns = useMemo<Array<DataTableColumn<User>>>(
      () => [
        {
          field: 'lastName',
          headerName: t('form_fields.last_name'),
          renderCell: (user) => (
            <div title={user.lastName} role={'presentation'} data-sentry-mask>
              {user.lastName}
            </div>
          ),
        },
        {
          field: 'firstName',
          headerName: t('form_fields.first_name'),
          renderCell: (user) => (
            <div title={user.firstName} role={'presentation'} data-sentry-mask>
              {user.firstName}
            </div>
          ),
        },
        {
          field: 'roles',
          headerName: t('form_fields.role'),
          valueGetter: (user) => user.userRoles?.map((role) => role.name).join(', '),
        },
        {
          field: 'externalId',
          headerName: t('form_fields.external_id'),
          valueGetter: (user) => user.externalId,
        },
        {
          defaultHidden: true,
          field: 'accountingId',
          headerName: t('form_fields.accounting_id'),
          valueGetter: (user) => user.accountingId,
        },
        {
          field: 'email',
          headerName: t('form_fields.email'),
          renderCell: (user) => (
            <div title={user.email ?? ''} role={'presentation'} data-sentry-mask>
              {user.email}
            </div>
          ),
        },
        {
          field: 'phone',
          headerName: t('form_fields.phone'),
          renderCell: (user) => {
            const formatted = formatPhoneForDisplay(user.phone);

            return (
              <div title={formatted} role={'presentation'} data-sentry-mask>
                {formatted}
              </div>
            );
          },
        },
        {
          field: 'timeZone',
          headerName: t('form_fields.time_zone'),
          valueGetter: (user) => user.timeZone,
        },
        {
          field: 'createdAt',
          headerName: t('common.created_on'),
          renderCell: (user) => (
            <Tooltip
              title={`${t('common.last_update')}: ${user.updatedAt ? dateFormat(user.updatedAt) : ''}`}
            >
              <Typography variant="body2">
                {user.createdAt ? dateFormat(user.createdAt) : ''}
              </Typography>
            </Tooltip>
          ),
        },
        actionsColumn((user) => {
          const options = [];

          // Resend email invitation
          if (!user.isActiveMember && user.email) {
            options.push({
              title: t('user.resend_email_invitation'),
              icon: <Mail />,
              callBack: () => resendInvite(user.id, 'email'),
            });
          }

          // Resend SMS invitation
          if (!user.isActiveMember && user.phone) {
            options.push({
              title: t('user.resend_sms_invitation'),
              icon: <Sms />,
              callBack: () => resendInvite(user.id, 'sms'),
            });
          }

          // Deactivate user
          if (userPermissions.canDeleteUser) {
            options.push({
              title: t('actions.deactivate'),
              icon: <ToggleOffOutlined />,
              callBack: () => setUserIdToDelete(user.id),
            });
          }

          return (
            <>
              {userPermissions.canEditUser && (
                <IconButton
                  aria-label={t('actions.edit')}
                  size="small"
                  onClick={() => editRow(user.id)}
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
              <SimpleMenu options={options}>
                <MoreHoriz />
              </SimpleMenu>
            </>
          );
        }),
      ],
      [userPermissions, managedCompanyId],
    );

    return (
      <Box>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('administration.managed_companies_v2.manage_users_for')}
            {managedCompany.legalName}
          </Typography>
          {userPermissions.canCreateUser && (
            <SmallButton
              color="brandV2Yellow"
              startIcon={<Add />}
              onClick={createNewUser}
            >
              {t('actions.add_user')}
            </SmallButton>
          )}
        </Box>
        <DataTable
          id="vendor-users-grid"
          columns={columns}
          rows={users}
          loading={
            isFetchingUsers || (Boolean(managedCompanyId) && !hasRequestedFirstPage)
          }
          onSearchChange={setSearchQuery}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isFetchingUsers}
              onNavigate={(link) => void goToUsersPage(link)}
              pagination={usersPagination}
            />
          }
        />
        <Dialog
          isOpen={userFormDialogState.isOpen}
          preventCloseOnBackdropClick
          preventCloseOnEscapeKeyDown
        >
          <DialogHeader onClose={handleClose}>
            {selectedId ? t('user.update_user') : t('user.create_user')}
          </DialogHeader>

          <DialogContent>
            <UserForm
              ref={updateUserDialogRef}
              allowSharing={true}
              availableAuthMethods={userFormDialogState.availableAuthMethods}
              companyShares={companyShares}
              defaultUser={selectedId ? selectedUser : null}
              editable={true}
              errors={asyncErrors}
              onFormStateChange={onFormStateChange}
              scopedCompanyId={companyId}
              managedCompanyId={managedCompanyId}
              isManagedUser={true}
            />

            {selectedUser?.id && (
              <UserNotificationsPreferenceForm
                key={selectedUser?.id}
                ref={userNotificationsPreferenceRef}
                onFormStateChange={onUserNotificationsPreferenceFormStateChange}
                user={selectedUser}
              />
            )}

            {invitesToSend && (
              <Box sx={{ mt: 2 }}>
                <InvitesToSendToggleGroup
                  invitesToSend={invitesToSend}
                  isUpdating={isUpdating}
                  onChangeInvite={onChangeInvite}
                />
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            {Boolean(asyncErrors.length) && (
              <ShakyErrorIndicator
                message={t('administration.users.user_form.invalid_data_error_messages')}
              />
            )}

            <SmallButton
              color="secondary"
              disabled={isUpdating}
              onClick={handleClose}
              variant="outlined"
            >
              {t('actions.cancel')}
            </SmallButton>

            <SmallLoadingButton
              loading={isUpdating}
              onClick={onSubmitForm}
              disabled={!isDirty}
            >
              {t(`actions.${selectedId ? 'update' : 'create'}`)}
            </SmallLoadingButton>
          </DialogActions>
        </Dialog>
        <DeactivateConfirmationDialog
          id={userIdToDelete}
          message={t('administration.users.deactivate_user_dialog_message')}
          onClose={() => setUserIdToDelete(undefined)}
          onConfirm={handleDeleteUser}
          title={t('administration.users.deactivate_user_dialog_title')}
        />
      </Box>
    );
  },
);
